import { QueryClient, QueryKey } from "@tanstack/react-query";
import { useAuth } from "../auth/auth";
import {
  AuthorizationTokenWithKeyPair,
  RequestSigner,
} from "@ncino/pa-authentication-client-webclient";
import { v4 as uuid } from "uuid";

export interface FetchError {
  message: string;
  canRetry: boolean;
}

export const queryClient = new QueryClient();

export const useFetchData = <TData, TVariables>(
  query: string,
  options?: RequestInit["headers"]
): ((variables?: TVariables) => Promise<TData>) => {
  // it is safe to call React Hooks here.
  const { tokenProvider } = useAuth();

  return fetchData<TData, TVariables>(query, tokenProvider, options);
};

export function useOptimisticUpdate<TData>(queryKey: QueryKey) {
  return {
    onMutate: async (updater: (old: TData | undefined) => TData | undefined) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TData>(queryKey);
      queryClient.setQueryData<TData>(queryKey, updater);
      return { previous };
    },
    onError: (
      _err: unknown,
      _vars: unknown,
      context: { previous: TData | undefined } | undefined
    ) => {
      queryClient.setQueryData<TData>(queryKey, context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  };
}

export const fetchData = <TData, TVariables>(
  query: string,
  tokenProvider: () => Promise<AuthorizationTokenWithKeyPair>,
  options?: RequestInit["headers"]
): ((variables?: TVariables) => Promise<TData>) => {
  // no React Hooks, use in loader / beforeload

  return async (variables?: TVariables) => {
    try {
      const response = await RequestSigner.signedFetch(
        import.meta.env.VITE_API_GATEWAY_URL,
        {
          method: "POST",
          body: JSON.stringify({
            query,
            variables,
          }),
          headers: {
            ...options,
          },
        },
        {
          bearerTokenProvider: tokenProvider,
          correlationId: uuid(),
        }
      );

      const result = await response.json();

      const extractErrorMessages = (
        errors: Array<{ message?: string; extensions?: { errors?: { message?: string }[] } }>
      ) =>
        errors.map((e) => {
          const nested = e.extensions?.errors;
          if (nested?.length) {
            return nested.map((n) => n.message ?? e.message ?? "Unknown error").join("; ");
          }
          return e.message ?? "Unknown error";
        });

      if (result.errors) {
        const messages = extractErrorMessages(result.errors);
        if (!result.data) {
          return Promise.reject({
            message: messages.join(", "),
            // In some cases, a network error/sudden timeout will return 504
            // which could be a temporary failure that can be retried again.
            canRetry: response.status === 504,
          });
        }
      }

      return result.data;
    } catch (err) {
      // fetch() throws itself upon network, a sudden abort, or browser issues
      // all of which can likely be retried again once those are fixed by the
      // user (if possible).
      return Promise.reject({
        message: (err as Error).message,
        canRetry: true,
      });
    }
  };
};
